import { getFileExtension } from '@/helpers/file';
import type { IVipTransferFormFailedData } from '@/modules/googleAnalytics';
import {
  type IVipFileError,
  VIP_FILE_ERROR,
  type VipFileErrorCode,
} from './components/VipFileUploader/helpers';

export const VIP_SERVER_ERROR_CODE = {
  serverError: 'server_error',
  emailNotSent: 'email_not_sent',
  networkError: 'network_error',
} as const;

export type VipServerErrorCode =
  (typeof VIP_SERVER_ERROR_CODE)[keyof typeof VIP_SERVER_ERROR_CODE];

const FILE_ERROR_CODE_PREFIX: Record<VipFileErrorCode, string> = {
  [VIP_FILE_ERROR.invalidFileType]: 'invalid_file_format',
  [VIP_FILE_ERROR.maxSize]: 'file_too_large',
  [VIP_FILE_ERROR.pdfMaxSize]: 'file_too_large',
};

export interface IVipFailedSlot {
  file: File | null;
  fileError: VipFileErrorCode | null;
}

export const toFailedSlot = (
  file: File | null,
  fileError: IVipFileError | null
): IVipFailedSlot => ({
  file: file ?? fileError?.file ?? null,
  fileError: fileError?.code ?? null,
});

interface IBuildVipFailedDataParams {
  file1: IVipFailedSlot;
  file2: IVipFailedSlot;
  serverError?: { code: VipServerErrorCode; rawError?: string } | null;
}

const getSlotCode = (slot: IVipFailedSlot, index: 1 | 2): string | null => {
  if (slot.fileError) {
    return `${FILE_ERROR_CODE_PREFIX[slot.fileError]}_file_${index}`;
  }
  if (!slot.file) return `missing_file_${index}`;
  return null;
};

const GENERIC_MIME_SUBTYPES = new Set(['octet-stream', '*']);

const getFormatFromMime = (file: File): string | null => {
  const subtype = file.type.split('/')[1]?.toLowerCase();
  if (!subtype || GENERIC_MIME_SUBTYPES.has(subtype)) return null;
  return subtype;
};

const getFormatFromName = (file: File): string | null =>
  getFileExtension(file.name ?? '')?.toLowerCase() ?? null;

const getFileFormat = (file: File | null): string | null => {
  if (!file) return null;
  return getFormatFromMime(file) ?? getFormatFromName(file) ?? 'unknown';
};

const getFileSizeKb = (file: File | null): number =>
  file ? Math.round(file.size / 1024) : 0;

export const buildVipFailedData = ({
  file1,
  file2,
  serverError,
}: IBuildVipFailedDataParams): IVipTransferFormFailedData => {
  const error_data = [getSlotCode(file1, 1), getSlotCode(file2, 2)].filter(
    (code): code is string => code !== null
  );

  if (serverError) error_data.push(serverError.code);

  return {
    error_data,
    file_1_attached: !!file1.file,
    file_2_attached: !!file2.file,
    file_1_format: getFileFormat(file1.file),
    file_2_format: getFileFormat(file2.file),
    file_1_size_kb: getFileSizeKb(file1.file),
    file_2_size_kb: getFileSizeKb(file2.file),
    ...(serverError?.rawError ? { error_data_raw: serverError.rawError } : {}),
  };
};
